import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import cardsData from "@/constants/cards.json";
import { Reading } from '../types/reading';
import { _getTextTarotReading, _getReadings, _getReadingById, _getPhotoTarotReading } from '../services/ReadingService';
import { useTranslation } from 'react-i18next';
import mixpanel from 'mixpanel-browser';

// Import the interface from ReadingService
interface TarotSpreadProps {
    cards: object;
    selectedReading?: {
        spread_type: string;
        matrix_size: string;
        layout: {
            position: string;
            card_name: string;
        }[];
        readingMode: string;
        focusArea: string;
        experienceLevel: string;
        question: string;
        notes: string;
    };
}

export interface TarotCard {
    id: number;
    card_name?: string;
    description?: {
        primary_meaning: string;
        interpretation: string;
        reversed: string;
    };
    visible?: boolean;
    reversed?: boolean;
}

// Helper function to get card category based on ID
const getCardCategory = (id: number): string => {
    if (id >= 0 && id <= 21) return 'majorArcana';
    if (id >= 22 && id <= 35) return 'cups';
    if (id >= 36 && id <= 49) return 'pentacles';
    if (id >= 50 && id <= 63) return 'swords';
    if (id >= 64 && id <= 77) return 'wands';
    return 'majorArcana'; // fallback
};

// Helper function to get translated card information
const getTranslatedCardHelper = (id: number, t: any): TarotCard => {
    const category = getCardCategory(id);
    const cardKey = id.toString();

    // Find the original card from cards.json (which now contains translation keys)
    const originalCard = cardsData.cards.find((card: any) => card.id === id);
    const reversed = t(originalCard?.reversed)

    // If we have the card data, translate it
    if (originalCard) {
        const translatedCard = {
            id,
            card_name: t(originalCard.card_name, { defaultValue: `Card ${id}` }),
            description: {
                primary_meaning: t(originalCard.primary_meaning, { defaultValue: '' }),
                interpretation: t(originalCard.interpretation, { defaultValue: '' }),
                reversed: reversed
            }
        }
        return translatedCard;
    }
};

const tarotDeck: TarotCard[] = Array.from(cardsData.cards).map((card: any) => ({
    id: card.id,
    card_name: card.card_name,
    description: {
        primary_meaning: card.primary_meaning,
        interpretation: card.interpretation,
    },
    visible: true,
}));

interface CardsContextType {
    drawnCards: TarotCard[];
    setDrawnCards: (cards: TarotCard[]) => void;
    visibleCards: { [key: number]: boolean };
    spreadType: string | null;
    setSpreadType: (type: string) => void;
    drawCards: (readingType?: any) => void;
    flipCard: (id: number) => void;
    getCards: () => TarotCard[];
    selectedReading: string | null;
    setSelectedReading: (reading: any) => void;
    readings: Reading[];
    reading: Reading | null;
    isLoading: boolean;
    error: string | null;
    getReadings: () => Promise<void>;
    getReadingById: (id: string) => Promise<Reading>;
    getTextTarotReading: (metadata: TarotSpreadProps) => Promise<void | null>;
    setReading: (reading: Reading | null) => void;
    photos: string[];
    setPhotos: (photos: string[]) => void;
    clearPhotos: () => void;
    getPhotos: () => Promise<string[]>;
    getPhotoTarotReading: (metadata: any) => Promise<void>;
    generateRandomCards: () => TarotCard[];
    getTranslatedCard: (id: number) => TarotCard;
    clearReadingCache: (id?: string) => void;
    isReadingCached: (id: string) => boolean;
}

// Helper function to handle unauthorized errors
const handleUnauthorizedError = (error: any) => {
    // Check if the error is an unauthorized error (401)
    if (error?.response?.status === 401 || error?.status === 401) {
        // Clear all localStorage data that might contain user-specific information
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('onboarding');
        // Keep appLanguage to persist language preference across sessions
        localStorage.removeItem('readingMetadata');
        localStorage.removeItem('selectedReading');
        localStorage.removeItem('drawnCards');
        localStorage.removeItem('dailyRandomCards');
        localStorage.removeItem('spreadType');
        localStorage.removeItem('formData');
        localStorage.removeItem('selectedReadingCategory');
        localStorage.removeItem('journalDraft');
        localStorage.removeItem('foodList');
        localStorage.removeItem('app_session');
        localStorage.removeItem('readings');
        localStorage.removeItem('lastReadingsSync');
        localStorage.removeItem('readingCache');

        // Clear sessionStorage data
        sessionStorage.clear();

        // Clear Mixpanel tracking data and reset user identification
        // Mixpanel stores data in localStorage with keys like mp_*, so we need to clear those
        // This ensures that user tracking data is completely removed and no analytics events
        // are associated with the logged-out user
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('mp_')) {
                localStorage.removeItem(key);
            }
        });

        // Reset Mixpanel user identification if possible
        try {
            if (mixpanel && typeof mixpanel.identify === 'function') {
                mixpanel.identify(''); // Clear user identification
            }

            // Clear any super properties that might have been set
            if (mixpanel && typeof mixpanel.register === 'function') {
                mixpanel.register({}); // Clear super properties
            }

            // Clear people properties if available
            if (mixpanel && mixpanel.people && typeof mixpanel.people.set === 'function') {
                mixpanel.people.set({}); // Clear people properties
            }
        } catch (error) {
            console.warn('Could not reset Mixpanel data:', error);
        }

        // Clear any other potential user data keys
        const keysToRemove = [
            'user_', 'reading_', 'card_', 'session_', 'auth_', 'profile_',
            'preference_', 'setting_', 'history_', 'cache_', 'temp_',
            'tracking_', 'analytics_', 'mixpanel_', 'mp_', 'ga_', 'gtag_'
        ];

        Object.keys(localStorage).forEach(key => {
            if (keysToRemove.some(pattern => key.startsWith(pattern))) {
                localStorage.removeItem(key);
            }
        });

        return true; // Indicate that unauthorized error was handled
    }

    return false; // Indicate that no unauthorized error was found
};

const CardsContext = createContext<CardsContextType | undefined>(undefined);

export const CardsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { t } = useTranslation();
    const [drawnCards, _setDrawnCards] = useState<TarotCard[]>([]);
    const [visibleCards, setvisibleCards] = useState<{ [key: number]: boolean }>({});
    const [selectedReading, _setSelectedReading] = useState<any | null>(
        JSON.parse(localStorage.getItem('selectedReading') || '{}')
    );
    const setSelectedReading = useCallback((newReading: any) => {
        _setSelectedReading(newReading);
        const currentReadingMetadata = localStorage.getItem('readingMetadata');
        if (currentReadingMetadata) {
            localStorage.setItem('readingMetadata', JSON.stringify({ ...JSON.parse(currentReadingMetadata), selectedReading: newReading }));
        } else {
            localStorage.setItem('readingMetadata', JSON.stringify(newReading));
        }
        // Don't clear cards here, let the Spread component handle that
    }, []);
    const [spreadType, _setSpreadType] = useState<string | null>(
        localStorage.getItem('spreadType')
    );

    const [readings, setReadings] = useState<Reading[]>([]);
    const [reading, setReading] = useState<Reading | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [photos, _setPhotos] = useState<string[]>([]);

    // Add cache and pending calls tracking
    const [readingCache, setReadingCache] = useState<{ [key: string]: Reading }>({});
    const [pendingCalls, setPendingCalls] = useState<{ [key: string]: Promise<Reading> }>({});

    const setSpreadType = useCallback((newSpread: string) => {
        _setSpreadType(newSpread);
        // Clear drawn cards when spread type changes
        _setDrawnCards([]);
        setvisibleCards({});
        // Don't clear selectedReading here anymore
    }, []);

    const setDrawnCards = useCallback((newDraw: TarotCard[]) => {
        const readingMetadata = JSON.parse(localStorage.getItem('readingMetadata') || '{}');
        const newDrawnMetadata = {
            question: readingMetadata.question,
            selectedReading: readingMetadata.selectedReading,
            cards: newDraw
        }
        console.log('newDrawnMetadata', newDrawnMetadata);
        _setDrawnCards(newDraw);
        localStorage.setItem('drawnCards', JSON.stringify(newDrawnMetadata));
    }, []);

    function shuffleDeck(deck = tarotDeck, flipProbability = 0.1) {
        const shuffledDeck = [...deck];

        // Fisher–Yates shuffle
        /**
            The Fisher–Yates shuffle (also called the Knuth shuffle) is the most accurate
            and unbiased way to randomly shuffle a list—such as a deck of cards.
            Every possible permutation is equally likely.

            🧠 Core Idea
            The algorithm walks through the array backward, and for each element,
            it swaps it with a random element that comes before it (or itself).

         */
        for (let i = shuffledDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledDeck[i], shuffledDeck[j]] = [shuffledDeck[j], shuffledDeck[i]];
        }

        // Apply flipping and set visible to false by default
        return shuffledDeck.map(card => ({
            ...card,
            visible: false,
            reversed: Math.random() < flipProbability
        }));
    }

    const drawCards = useCallback((readingType?: any) => {

        const readingMetadata = JSON.parse(localStorage.getItem('readingMetadata') || '{}');
        const selectedReading = readingType || readingMetadata.selectedReading || reading;
        console.log('drawCards', readingMetadata);
        // Only use existing cards if selectedReading has cards property
        if (selectedReading && selectedReading.cards && Array.isArray(selectedReading.cards)) {
            setDrawnCards(selectedReading.cards.map((card: any) => ({
                ...card,
                visible: false
            })));
            return;
        }
        // Ensure we have a selectedReading or use defaults
        if (!selectedReading) {
            console.warn('No selectedReading found, using default 3-card spread');
        }

        // Fallback to default values if selectedReading doesn't have the expected structure
        const cardCount = selectedReading?.count || 3;
        const totalCards = selectedReading?.totalCards || 9;
        const setup = selectedReading?.setup || [0, 1, 2];

        const newDraw = shuffleDeck().slice(0, cardCount);
        console.log('Drawing cards for spread:', selectedReading?.type, 'Cards:', newDraw);

        // For certain spread types, store cards in simple array format
        const specialSpreadTypes = ["celtic-cross", "horseshoe-spread", "tetraktys-spread", "tree-of-life", "astrological-spread", "year-ahead-spread", "three-card"];

        if (selectedReading?.type && specialSpreadTypes.includes(selectedReading.type)) {
            // For special spread types, store cards directly
            setDrawnCards(newDraw);
        } else {
            // For grid-based spreads, use the original matrix logic
            let deckClearMatrix = Array(totalCards).fill(null);

            let iCounter = 0;
            for (let i = 0; i < deckClearMatrix.length; i++) {
                if (setup.includes(i)) {
                    deckClearMatrix[i] = newDraw[iCounter];
                    iCounter++;
                }
            }

            setDrawnCards(newDraw);
        }
    }, [reading, setDrawnCards]); // Add setDrawnCards as dependency

    const flipCard = useCallback((id: number) => {
        setvisibleCards((prev) => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const getCards = useCallback(() => {
        return drawnCards;
    }, [drawnCards]);

    const getReadings = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result = await _getReadings();
            setReadings(result);
        } catch (err) {
            // Handle unauthorized error
            if (handleUnauthorizedError(err)) {
                return; // Stop execution if unauthorized error was handled
            }
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    }, []);

    const getReadingById = useCallback(async (id: string) => {
        // Check if reading is already in cache
        if (readingCache[id]) {
            console.log('Reading found in cache:', id);
            setReading(readingCache[id]);
            return readingCache[id];
        }

        // Check if there's already a pending call for this ID
        if (id in pendingCalls) {
            console.log('Reading call already in progress, waiting for result:', id);
            try {
                const result = await pendingCalls[id];
                setReading(result);
                return result;
            } catch (err) {
                throw err;
            }
        }

        console.log('Making new API call for reading:', id);
        setIsLoading(true);
        setError(null);

        // Create the API call promise
        const apiCall = (async () => {
            try {
                const result = await _getReadingById(id);

                // Store in cache
                setReadingCache(prev => ({
                    ...prev,
                    [id]: result
                }));

                setReading(result);
                return result;
            } catch (err) {
                // Handle unauthorized error
                if (handleUnauthorizedError(err)) {
                    throw err; // Re-throw to stop execution
                }
                setError((err as Error).message);
                throw err;
            } finally {
                setIsLoading(false);
                // Remove from pending calls when done
                setPendingCalls(prev => {
                    const newPending = { ...prev };
                    delete newPending[id];
                    return newPending;
                });
            }
        })();

        // Store the pending call
        setPendingCalls(prev => ({
            ...prev,
            [id]: apiCall
        }));

        return apiCall;
    }, [readingCache, pendingCalls]);

    const getTextTarotReading = useCallback(async (metadata: TarotSpreadProps) => {
        setIsLoading(true);
        setError(null);
        const readingMetadata = JSON.parse(localStorage.getItem('readingMetadata') || '{}');
        const question = readingMetadata.question;
        const payload = {
            cards: drawnCards,
            question: question,
            metadata: metadata.selectedReading
        }
        console.log('payload', payload);
        try {
            const result = await _getTextTarotReading(payload);
            return result;
        } catch (err) {
            // Handle unauthorized error
            if (handleUnauthorizedError(err)) {
                return null; // Stop execution if unauthorized error was handled
            }
            setError((err as Error).message);
            return null;
        } finally {
            setIsLoading(false);
        }
    }, [drawnCards]);

    const setPhotos = useCallback((newPhotos: string[]) => {
        _setPhotos(newPhotos);
    }, []);

    const getPhotos = useCallback(async () => {
        return photos;
    }, [photos]);

    const clearPhotos = useCallback(() => {
        setPhotos([]);
    }, [setPhotos]);

    const getPhotoTarotReading = useCallback(async (metadata: any) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await _getPhotoTarotReading({
                photos,
                metadata
            });
            return result;
        } catch (err) {
            // Handle unauthorized error
            if (handleUnauthorizedError(err)) {
                return; // Stop execution if unauthorized error was handled
            }
            setError((err as Error).message);
        } finally {
            setIsLoading(false);
        }
    }, [photos]);

    const generateRandomCards = useCallback(() => {
        const today = new Date().toISOString().split('T')[0];
        const storedData = localStorage.getItem('dailyRandomCards');

        if (storedData) {
            const { date, cards } = JSON.parse(storedData);
            if (date === today) {
                return cards;
            }
        }

        const usedIds = new Set<number>();
        const randomCards: TarotCard[] = [];

        while (randomCards.length < 3) {
            const id = Math.floor(Math.random() * 78) + 1;

            if (!usedIds.has(id)) {
                usedIds.add(id);
                const card = getTranslatedCardHelper(id, t);
                randomCards.push({
                    ...card,
                    visible: false,
                    reversed: Math.random() < 0.5
                });
            }
        }

        // Store the new cards with today's date
        localStorage.setItem('dailyRandomCards', JSON.stringify({
            date: today,
            cards: randomCards
        }));

        return randomCards;
    }, [t]);

    // Function to get translated card information
    const getTranslatedCard = useCallback((id: number): TarotCard => {
        return getTranslatedCardHelper(id, t);
    }, [t]);

    // Cache management functions
    const clearReadingCache = useCallback((id?: string) => {
        if (id) {
            setReadingCache(prev => {
                const newCache = { ...prev };
                delete newCache[id];
                return newCache;
            });

            setPendingCalls(prev => {
                const newPending = { ...prev };
                delete newPending[id];
                return newPending;
            });
        } else {
            setReadingCache({});
            setPendingCalls({});
        }
    }, []);

    const isReadingCached = useCallback((id: string): boolean => {
        return id in readingCache;
    }, [readingCache]);







    // Remove the selectedReading useEffect that was causing the loop
    // Instead, store in localStorage only when explicitly setting the reading
    useEffect(() => {
        if (spreadType) {
            localStorage.setItem('spreadType', spreadType);
        } else {
            localStorage.removeItem('spreadType');
        }
    }, [spreadType]);

    return (
        <CardsContext.Provider value={{
            drawnCards,
            setDrawnCards,
            visibleCards,
            spreadType,
            setSpreadType,
            drawCards,
            flipCard,
            getCards,
            selectedReading,
            setSelectedReading,
            readings,
            reading,
            isLoading,
            error,
            getReadings,
            getReadingById,
            getTextTarotReading,
            setReading,
            photos,
            setPhotos,
            clearPhotos,
            getPhotos,
            getPhotoTarotReading,
            generateRandomCards,
            getTranslatedCard,
            clearReadingCache,
            isReadingCached,
        }}>
            {children}
        </CardsContext.Provider>
    );
};

export default CardsContext; 